import React, { useEffect, useState, useCallback, useRef } from 'react'
import { useParams, useHistory } from 'react-router-dom'
import { useSnackbar } from 'react-simple-snackbar'
import { downloadInvoicePdf, printInvoicePdf } from '../../utils/invoicePdf'
import * as api from '../../api/index'
import { useRhGuard } from '../../hooks/useRoleGuard'
import Spinner from '../Spinner/Spinner'
import styles from './Coaches.module.css'

const MONTHS = [
  '', 'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre',
]

const formatTND = (n) =>
  Number(n || 0).toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })

const formatPeriod = (month, year) => {
  if (!month || !year) return '—'
  return `${MONTHS[month]} ${year}`
}

const statusBadge = (status) => {
  if (status === 'sent') return { cls: styles.badgeOk, text: 'Envoyée' }
  if (status === 'failed') return { cls: styles.badgeErr, text: 'Échec' }
  return { cls: styles.badgePending, text: 'En attente' }
}

const CoachDetail = () => {
  const { id } = useParams()
  const history = useHistory()
  const [openSnackbar] = useSnackbar()
  const openSnackbarRef = useRef(openSnackbar)
  openSnackbarRef.current = openSnackbar
  useRhGuard()
  const [loading, setLoading] = useState(true)
  const [data, setData] = useState(null)
  const [page, setPage] = useState(1)
  const limit = 10

  const loadCoach = useCallback(async () => {
    setLoading(true)
    try {
      const { data: result } = await api.fetchCoach(id, { page, limit })
      setData(result)
    } catch (error) {
      openSnackbarRef.current(error?.response?.data?.message || 'Coach introuvable')
      history.push('/coaches')
    } finally {
      setLoading(false)
    }
  }, [id, page, history])

  useEffect(() => {
    loadCoach()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, page])

  const downloadPdf = async (invoiceId, campaignId, invoiceNumber) => {
    if (!campaignId) {
      openSnackbar('PDF non disponible')
      return
    }
    try {
      await downloadInvoicePdf(campaignId, invoiceId, invoiceNumber)
    } catch {
      openSnackbar('Erreur téléchargement PDF')
    }
  }

  const printPdf = async (invoiceId, campaignId) => {
    if (!campaignId) {
      openSnackbar('PDF non disponible')
      return
    }
    try {
      await printInvoicePdf(campaignId, invoiceId)
    } catch {
      openSnackbar('Erreur impression PDF')
    }
  }

  if (loading && !data) {
    return (
      <div className={styles.loading}>
        <Spinner />
      </div>
    )
  }

  if (!data?.coach) return null

  const { coach, invoices = [], pagination } = data

  return (
    <div className={styles.page}>
      <button type="button" className={styles.back} onClick={() => history.push('/coaches')}>
        ← Retour aux coaches
      </button>

      <div className={styles.profileCard}>
        <div className={styles.profileAvatar}>{coach.name?.charAt(0)?.toUpperCase()}</div>
        <div className={styles.profileInfo}>
          <h1>{coach.name}</h1>
          <div className={styles.profileMeta}>
            <span>MF : {coach.matriculeFiscal}</span>
            <span>{coach.email}</span>
            <span>{coach.address || 'Adresse non renseignée'}</span>
          </div>
        </div>
      </div>

      <div className={styles.kpiGrid}>
        <div className={`${styles.kpiCard} ${styles.kpiPrimary}`}>
          <span className={styles.kpiLabel}>Factures</span>
          <span className={styles.kpiValue}>{coach.invoiceCount || 0}</span>
        </div>
        <div className={styles.kpiCard}>
          <span className={styles.kpiLabel}>Honoraires cumulés</span>
          <span className={styles.kpiValue}>{formatTND(coach.totalHonoraires)} TND</span>
        </div>
        <div className={styles.kpiCard}>
          <span className={styles.kpiLabel}>Net cumulé</span>
          <span className={styles.kpiValue}>{formatTND(coach.totalNet)} TND</span>
        </div>
        <div className={styles.kpiCard}>
          <span className={styles.kpiLabel}>Dernière campagne</span>
          <span className={styles.kpiValue} style={{ fontSize: '1.1rem' }}>
            {coach.periodLabel || '—'}
          </span>
        </div>
      </div>

      <div className={styles.panel}>
        <div className={styles.panelHeader}>
          <div>
            <h2>Historique des factures</h2>
            <p className={styles.hint}>{pagination?.total || 0} facture(s) pour ce coach</p>
          </div>
        </div>

        {invoices.length === 0 ? (
          <p className={styles.empty}>Aucune facture enregistrée pour ce coach.</p>
        ) : (
          <>
            <div className={styles.tableWrap}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>N° Facture</th>
                    <th>Période</th>
                    <th>Honoraires</th>
                    <th>Net à payer</th>
                    <th>Statut envoi</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map((inv) => {
                    const st = statusBadge(inv.emailStatus)
                    return (
                      <tr key={inv._id}>
                        <td>{inv.invoiceNumber}</td>
                        <td>{formatPeriod(inv.periodMonth, inv.periodYear)}</td>
                        <td>{formatTND(inv.honoraires)} TND</td>
                        <td>{formatTND(inv.netAPayer)} TND</td>
                        <td><span className={st.cls}>{st.text}</span></td>
                        <td>
                          <button
                            type="button"
                            className={styles.tableBtn}
                            onClick={() => downloadPdf(inv._id, inv.campaignId, inv.invoiceNumber)}
                          >
                            PDF
                          </button>
                          <button
                            type="button"
                            className={styles.tableBtn}
                            style={{ marginLeft: 8 }}
                            onClick={() => printPdf(inv._id, inv.campaignId)}
                          >
                            Imprimer
                          </button>
                          {inv.campaignId && (
                            <button
                              type="button"
                              className={styles.tableBtn}
                              style={{ marginLeft: 8 }}
                              onClick={() => history.push(`/campagnes/${inv.campaignId}`)}
                            >
                              Campagne
                            </button>
                          )}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>

            {pagination?.totalPages > 1 && (
              <div className={styles.pagination}>
                <button
                  type="button"
                  className={styles.pageBtn}
                  disabled={page <= 1 || loading}
                  onClick={() => setPage((p) => p - 1)}
                >
                  ← Précédent
                </button>
                <span className={styles.pageLabel}>
                  Page {pagination.page} sur {pagination.totalPages}
                </span>
                <button
                  type="button"
                  className={styles.pageBtn}
                  disabled={page >= pagination.totalPages || loading}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Suivant →
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

export default CoachDetail
